import { Link } from "react-router-dom";
import { ArrowRight, Users, Building2, Crown, Gift, Sparkles, Phone, CalendarClock } from "lucide-react";
import { Reveal, MaskLines, ImageReveal, VideoWithSound } from "../components/motion/Reveal.jsx";
import EnquiryForm from "../components/EnquiryForm.jsx";
import useSeo from "../hooks/useSeo.js";
import { MEMBERSHIPS, FAMILY_FOUR } from "../content.js";

const EXPERIENCE = ["Gym", "Swimming", "Sports", "Group Classes", "Kids Activities", "Recovery", "Community"];

export default function BlueMembership() {
  useSeo({
    title: "Blue Membership | One Family of Four, Up to 25 Years — Oneiros Sports Club Surat",
    description:
      "Blue Membership — a single-club membership for your family of four at Oneiros Sports Club Surat: 12 years at Katargam, 15 years at Adajan, 25 years at Varachha. Prefer every club? Gold opens all three. Enquire from this page.",
    image: "/oneiros/blue-family-four.jpg",
  });

  return (
    <div data-testid="blue-membership-page" className="bg-royal text-white">
      {/* HERO — family of four */}
      <section className="grain relative overflow-hidden">
        <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 pb-20 pt-36 lg:grid-cols-12 lg:px-12 lg:pt-48">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-10 bg-brand" />
                Oneiros Memberships
              </p>
            </Reveal>
            <MaskLines
              lines={["Gift Your Family of", "Years of Fitness", "& Wellness."]}
              className="mt-6 font-display text-[clamp(2.3rem,5.4vw,4.6rem)] leading-[1.02]"
              delay={0.1}
            />
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
                One membership, four members, years of active life together. Choose <span className="font-bold text-white">Blue</span> for your
                home club — 12 years at Katargam, 15 at Adajan, 25 at Varachha — or <span className="font-bold text-white">Gold</span> and let
                every Oneiros door in Surat open for your family.
              </p>
            </Reveal>
            <Reveal delay={0.4} className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a
                data-testid="blue-hero-enquire-btn"
                href="#blue-enquiry"
                className="group inline-flex w-fit items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
              >
                Start Membership Enquiry
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                data-testid="blue-hero-clubs-btn"
                to="/our-clubs"
                className="inline-flex w-fit items-center rounded-full border border-white/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:border-white hover:bg-white hover:text-navy"
              >
                Visit a Club
              </Link>
            </Reveal>
          </div>
          <div className="relative lg:col-span-5">
            <ImageReveal src="/oneiros/blue-family-four.jpg" alt="A family of four at Oneiros Sports Club" className="aspect-[4/3] w-full" />
            <Reveal delay={0.35} className="absolute -bottom-6 -left-2 bg-navy px-6 py-4 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.6)] md:left-6">
              <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-brand">
                <Users className="h-3.5 w-3.5" /> One family of four
              </p>
              <p className="mt-1 font-display text-2xl">Up to 25 years together</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BLUE MEMBERSHIP LOCKUP — from the official creative */}
      <section data-testid="blue-wordmark-band" className="border-b border-white/10 py-16">
        <Reveal className="mx-auto max-w-md px-6">
          <img
            src="/oneiros/blue-wordmark-gold.png"
            alt="Blue Membership — official Oneiros creative"
            className="w-full rounded-xl border border-white/15 object-contain"
          />
        </Reveal>
      </section>

      {/* TENURE BY CLUB */}
      <section data-testid="blue-tenure-section" className="border-y border-white/10 py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="text-center">
            <Reveal>
              <p className="eyebrow justify-center">Blue Membership Tenure</p>
            </Reveal>
            <MaskLines lines={["Pick Your Club.", "Pick Your Years."]} align="center" className="justify-center text-center font-display text-4xl leading-[1.05] sm:text-5xl" />
            <Reveal delay={0.2}>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/60 md:text-base">
                The longer the promise, the stronger the bond — your family of four trains at your home club for the full tenure.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {MEMBERSHIPS.blue.tenures.map((t, i) => (
              <Reveal key={t.club} delay={i * 0.08}>
                <div data-testid={`blue-tenure-${t.short.toLowerCase()}`} className="group flex h-full flex-col items-center gap-2 border border-white/12 bg-white/[0.03] px-8 py-12 text-center transition-colors hover:border-brand/60">
                  <CalendarClock className="h-5 w-5 text-brand" />
                  <p className="font-display text-6xl text-white md:text-7xl">{t.years.split(" ")[0]}</p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-brand">Years</p>
                  <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-white/80">{t.club}</p>
                  <a data-testid={`blue-tenure-cta-${t.short.toLowerCase()}`} href="#blue-enquiry" className="mt-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 underline-offset-4 hover:text-brand hover:underline">
                    Enquire for {t.short}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLUE vs GOLD — official leaflet card visuals */}
      <section data-testid="membership-compare" className="grain bg-navy-obsidian py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="text-center">
            <Reveal>
              <p className="eyebrow justify-center">Two Ways to Belong</p>
            </Reveal>
            <MaskLines lines={["One Home Club, or", "Every Oneiros Door."]} align="center" className="justify-center text-center font-display text-4xl leading-[1.05] sm:text-5xl" />
          </div>
          <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-2">
            {[MEMBERSHIPS.blue, MEMBERSHIPS.gold].map((m, i) => (
              <Reveal key={m.id} delay={i * 0.1}>
                <div
                  data-testid={`membership-card-${m.id}`}
                  className={`relative flex h-full flex-col overflow-hidden rounded-xl ${i === 1 ? "border border-brand/50 bg-white/[0.04]" : "border border-white/12 bg-white/[0.03]"}`}
                >
                  <span className={`absolute right-5 top-5 z-10 flex items-center gap-2 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.2em] ${i === 1 ? "bg-brand text-white" : "bg-navy/85 text-white backdrop-blur"}`}>
                    {i === 1 ? <Crown className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                    {i === 1 ? "All 3 Clubs" : "One Home Club"}
                  </span>
                  <div className="relative overflow-hidden bg-white">
                    <img src={m.cardImage} alt={m.cardImageAlt} loading="lazy" className="w-full h-auto object-contain transition-transform duration-700 hover:scale-[1.02]" />
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-7 md:p-9">
                    <p className="eyebrow">{m.tagline}</p>
                    <h3 className="font-display text-4xl">{m.name}</h3>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/60">
                      {m.scope} · {m.familyNote}
                    </p>
                    <ul className="mt-1 flex flex-col gap-2.5">
                      {m.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-white/80">
                          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {b}
                        </li>
                      ))}
                    </ul>
                    <a
                      data-testid={`membership-enquire-${m.id}`}
                      href="#blue-enquiry"
                      className={`mt-auto inline-flex w-fit items-center gap-2 rounded-full px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${
                        i === 1 ? "bg-brand text-white hover:bg-brand-dark" : "bg-white text-navy hover:bg-brand hover:text-white"
                      }`}
                    >
                      Enquire — {m.name.split(" ")[0]}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p data-testid="blue-launch-offer" className="mx-auto mt-10 flex max-w-3xl items-center justify-center gap-3 border border-brand/40 bg-white/[0.03] px-6 py-4 text-center text-xs leading-relaxed text-white/80">
              <Gift className="h-4 w-4 shrink-0 text-brand" />
              {MEMBERSHIPS.launchOffer}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAMILY OF FOUR */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="text-center">
            <Reveal>
              <p className="eyebrow justify-center">The Family Promise</p>
            </Reveal>
            <MaskLines lines={["One Membership. Four Members.", "Years of Togetherness."]} align="center" className="justify-center text-center font-display text-4xl leading-[1.05] sm:text-5xl" />
            <Reveal delay={0.2}>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/60 md:text-base">
                Structured. Safe. Effective. Long-term. Blue Membership is built around exactly one family of four — every plan, class and court
                designed for the four of you, for your club's full tenure.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {FAMILY_FOUR.map((f, i) => (
              <Reveal key={f.who} delay={i * 0.08}>
                <div data-testid={`blue-family-${i}`} className="flex h-full flex-col gap-3 border border-white/12 bg-white/[0.03] p-8 transition-colors hover:border-brand/50">
                  <span className="font-display text-3xl text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display text-2xl">{f.who}</p>
                  <p className="text-sm leading-relaxed text-white/65">{f.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO BAND — official membership film with sound */}
      <section className="relative border-y border-white/10">
        <div className="mx-auto grid max-w-[1440px] items-center gap-0 lg:grid-cols-2">
          <div className="px-6 py-20 lg:px-16 lg:py-28">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> The Experience
              </p>
            </Reveal>
            <MaskLines lines={["More Time Together.", "More Active Years.", "More of What Matters."]} className="mt-6 font-display text-3xl leading-[1.1] sm:text-4xl" />
            <ul className="mt-8 flex flex-wrap gap-2.5">
              {EXPERIENCE.map((e) => (
                <li key={e} className="border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-white/75">
                  {e}
                </li>
              ))}
            </ul>
          </div>
          <VideoWithSound
            src="/oneiros/blue-membership-720.mp4"
            poster="/oneiros/poster-blue.jpg"
            testId="blue-membership-film"
            className="aspect-[4/3] w-full lg:aspect-auto lg:min-h-[520px]"
          />
        </div>
      </section>

      {/* ON-PAGE ENQUIRY — no club dropdown */}
      <section id="blue-enquiry" data-testid="blue-enquiry-section" className="grain bg-royal py-24 lg:py-32">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> Enquire Here
              </p>
            </Reveal>
            <MaskLines lines={["Begin Your Family's", "Years Together."]} className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl" />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-white/65 md:text-base">
                Share your details and our membership team will call you back — with current plans, the tenure for your preferred club, and the
                Blue Moon launch benefit.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-8 flex items-center gap-3 border border-white/15 p-5 text-sm text-white/75">
                <Phone className="h-4 w-4 shrink-0 text-brand" />
                Prefer to talk? Adajan: +91 76900 94003 · Katargam: +91 63518 97993
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-white/12 bg-white/[0.04] p-8 md:p-10">
                <EnquiryForm dark testId="blue-page-form" source="Blue Membership Page" extraOptions={[{ value: "Gold — All Oneiros Clubs", label: "Gold Membership (All Clubs)" }]} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL — family background with highlighted text */}
      <section className="relative overflow-hidden py-28 text-center text-white lg:py-36" data-testid="blue-final-statement">
        <img
          src="/oneiros/blue-family-four.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-royal/85 via-navy/75 to-royal/90" />
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center gap-8 px-6">
          <Reveal>
            <p className="eyebrow justify-center">The Oneiros Promise</p>
          </Reveal>
          <MaskLines
            lines={["One Family.", "One Membership.", "Your Club, Your Years."]}
            align="center"
            className="justify-center text-center font-display text-[clamp(2.4rem,6vw,5rem)] leading-[1.02] drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
          />
          <Reveal delay={0.25} className="flex flex-col items-center gap-4 sm:flex-row">
            <a
              data-testid="blue-final-enquire-btn"
              href="#blue-enquiry"
              className="rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
            >
              Enquire Now
            </a>
            <Link
              data-testid="blue-final-clubs-btn"
              to="/our-clubs"
              className="rounded-full border border-white/40 bg-navy/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur transition-colors hover:border-white hover:bg-white hover:text-navy"
            >
              Visit a Club
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
